import React, { ChangeEventHandler, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { AnimatePresence } from 'framer-motion';
import { useDebounce } from 'use-debounce';
import { useQuery } from 'react-query';
import qs from 'qs';
import urljoin from 'url-join';
import subDays from 'date-fns/subDays';
import isValid from 'date-fns/isValid';
import {
  StyledForm,
  StyledTableWrapper,
  StyledKpiStrip,
  StyledCustomerGrid,
  StyledCustomerCardItem,
  StyledCustomerAvatar,
  StyledCustomerName,
  StyledCustomerSubtitle,
  StyledOrderCount,
  StyledValueCell,
} from './styles';
import Avatar from '../../components/atoms/Avatar';
import apiRoutes from '../../setup/consts/apiRoutes';
import useHeaderTitleContext from '../../hooks/useHeaderTitleContext';
import { GetCustomersResponseSingleItem } from '../../setup/apiTypes/customers';
import DataTable from '../../components/molecules/Table/dataTable';
import { customersTableColumns, avatarPalette, getInitials } from './consts';
import CustomerDetailsModal from '../../components/organisms/Modal/templates/CustomerDetailsModal';
import Input from '../../components/atoms/Fields/Input';
import { FieldMode } from '../../components/atoms/Fields/enums';
import { IconCalendar, IconClose, IconDownload, IconSearch, IconGrid, IconList, IconMapPin, IconFilter, IconPlus } from '../../assets/svg/icons';
import Spinner from '../../components/atoms/Spinner';
import { SpinnerMode, SpinnerSize } from '../../components/atoms/Spinner/enums';
import { StyledClearButton } from '../../components/atoms/Fields/Autocomplete/styles';
import { DataTableAPI, ListingEndpointResponse } from '../../components/molecules/Table/types';
import ScPageHeader from '../../components/molecules/ScPageHeader';
import {
  StyledToolbar,
  StyledToolbarCount,
  StyledToolbarFilters,
  StyledToolbarGhostButton,
  StyledToolbarSearch,
  StyledRemovableChip,
  StyledFilterChip,
  StyledFilterDropdownPanel,
} from '../../components/molecules/Toolbar';
import SimpleSelect from '../../components/atoms/Fields/Select/templates/SimpleSelect';
import { useCitiesList } from '../../hooks/useCitiesList';
import useToggle from '../../hooks/useToggle';
import { IconChevronDown } from '../../assets/svg/icons';
import ScStat from '../../components/molecules/ScStat';
import ScCard from '../../components/atoms/ScCard';
import Button from '../../components/atoms/Button';
import { ButtonKind, ButtonSize } from '../../components/atoms/Button/enums';

const CustomerPage: React.FC = () => {
  const [detailsCustomerId, setDetailsCustomerId] = useState<string>();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [minOrdersStr, setMinOrdersStr] = useState<string>('0');
  const [activeOnly, setActiveOnly] = useState<boolean>(false);
  const [isGridView, setIsGridView] = useState<boolean>(false);
  const [customersTableAPI, setCustomersTableAPI] = useState<DataTableAPI>();

  const { setHeaderTitle } = useHeaderTitleContext();
  const intl = useIntl();

  const MIN_ORDERS_OPTIONS = useMemo(() => [
    { value: '0', label: intl.formatMessage({ id: 'customers.anyOrders', defaultMessage: 'Any orders' }) },
    { value: '2', label: '2+' },
    { value: '5', label: '5+' },
    { value: '10', label: '10+' },
    { value: '25', label: '25+' },
  ], [intl]);

  const tableColumns = useMemo(() => customersTableColumns(intl), [intl]);
  const [debouncedQuery] = useDebounce(searchQuery, 400);
  const { list: cityOptions } = useCitiesList();
  const [isCityOpen, { toggle: toggleCity, toggleOff: closeCity, registerContainerRef: cityRef }] = useToggle();

  const handleSearchQueryChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCity('');
    setMinOrdersStr('0');
    setActiveOnly(false);
  };

  useEffect(() => {
    setHeaderTitle(
      intl.formatMessage({
        id: 'mainNavigation.customer',
        defaultMessage: 'Customers',
      })
    );
  }, [intl, setHeaderTitle]);

  const minOrders = parseInt(minOrdersStr, 10) || 0;
  const totalCount = customersTableAPI?.totalCount;
  const hasActiveFilters = !!searchQuery || !!selectedCity || minOrders > 0 || activeOnly;

  const selectedCityName = useMemo(
    () => cityOptions.find((o) => o.value === selectedCity)?.label ?? '',
    [cityOptions, selectedCity]
  );

  const luceneQuery = useMemo(() => {
    const parts: string[] = [];
    if (debouncedQuery) parts.push(debouncedQuery);
    if (selectedCityName) parts.push(`addresses.state:${selectedCityName.toLowerCase()}`);
    return parts.join(' AND ');
  }, [debouncedQuery, selectedCityName]);

  const combinedFilterFn = useMemo(() => {
    const fns: ((item: GetCustomersResponseSingleItem) => boolean)[] = [];
    if (selectedCityName) {
      fns.push(
        (item) =>
          item.addresses?.[0]?.state?.toLowerCase() === selectedCityName.toLowerCase()
      );
    }
    if (minOrders > 0) {
      fns.push((item) => (item.totalOrders ?? 0) >= minOrders);
    }
    if (activeOnly) {
      const cutoff = subDays(new Date(), 30);
      fns.push((item) => {
        const d = new Date(item.lastOrderDate);
        return isValid(d) && d >= cutoff;
      });
    }
    if (fns.length === 0) return undefined;
    return (item: GetCustomersResponseSingleItem) => fns.every((fn) => fn(item));
  }, [selectedCityName, minOrders, activeOnly]);

  const gridQueryStr = useMemo(
    () =>
      qs.stringify(
        { ...(luceneQuery ? { query: luceneQuery } : {}), pageIndex: 1, pageSize: 24 },
        { addQueryPrefix: true }
      ),
    [luceneQuery]
  );

  const { data: gridData, isLoading: gridLoading } = useQuery<
    UseQueryResponse<ListingEndpointResponse<GetCustomersResponseSingleItem>>
  >(urljoin(apiRoutes.CUSTOMERS, gridQueryStr), { enabled: isGridView });

  const gridRows = useMemo(() => {
    const items = gridData?.response?.items ?? [];
    return combinedFilterFn ? items.filter(combinedFilterFn) : items;
  }, [gridData, combinedFilterFn]);

  return (
    <>
      <ScPageHeader
        title={intl.formatMessage({ id: 'mainNavigation.customers', defaultMessage: 'Customers' })}
        subtitle={
          totalCount !== undefined
            ? intl.formatMessage({ id: 'customers.pageSubtitleCount', defaultMessage: '{count} customers total' }, { count: totalCount.toLocaleString() })
            : intl.formatMessage({ id: 'customers.pageSubtitle', defaultMessage: 'Customers across all entities' })
        }
        actions={
          <>
            <Button kind={ButtonKind.Outline} icon={IconDownload}>
              {intl.formatMessage({ id: 'customers.export', defaultMessage: 'Export' })}
            </Button>
            <Button kind={ButtonKind.Primary} icon={IconPlus}>
              {intl.formatMessage({ id: 'customers.addCustomer', defaultMessage: 'Add customer' })}
            </Button>
          </>
        }
      />

      {/* KPI Strip */}
      <StyledKpiStrip>
        <ScStat
          label={intl.formatMessage({ id: 'customers.allCustomers', defaultMessage: 'All customers' })}
          value={totalCount !== undefined ? totalCount.toLocaleString() : '—'}
          isLoading={!customersTableAPI && totalCount === undefined}
          sub={intl.formatMessage({ id: 'customers.lifetime', defaultMessage: 'Lifetime' })}
          accent='ink'
        />
        <ScStat
          label={intl.formatMessage({ id: 'customers.citiesCovered', defaultMessage: 'Cities covered' })}
          value={cityOptions.length > 0 ? cityOptions.length : '—'}
          isLoading={cityOptions.length === 0}
          sub={intl.formatMessage({ id: 'customers.activeShippingCities', defaultMessage: 'Active shipping cities' })}
          accent='green'
        />
        {hasActiveFilters && (
          <ScStat
            label={intl.formatMessage({ id: 'customers.filteredResults', defaultMessage: 'Filtered results' })}
            value={totalCount !== undefined ? totalCount.toLocaleString() : '—'}
            isLoading={!customersTableAPI && totalCount === undefined}
            sub={intl.formatMessage({ id: 'customers.matchCurrentFilters', defaultMessage: 'Match current filters' })}
            accent='warning'
          />
        )}
      </StyledKpiStrip>

      <ScCard pad={false} style={{ padding: 'var(--s-4)', marginBottom: 'var(--s-4)' }}>
      <StyledToolbar style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
        <StyledToolbarSearch>
          <StyledForm onSubmit={(e) => e.preventDefault()}>
            <Input
              mode={FieldMode.Tertiary}
              placeholder={intl.formatMessage({
                id: 'search.searchCustomers',
                defaultMessage: 'Search by name, phone, or last order…',
              })}
              isRounded
              prefixComponent={<IconSearch />}
              suffixComponent={
                customersTableAPI?.loading && searchQuery ? (
                  <Spinner mode={SpinnerMode.Dark} size={SpinnerSize.ExtraSmall} />
                ) : searchQuery ? (
                  <StyledClearButton
                    type='button'
                    aria-label='Clear search'
                    onClick={() => setSearchQuery('')}
                  >
                    <IconClose />
                  </StyledClearButton>
                ) : null
              }
              value={searchQuery}
              onChange={handleSearchQueryChange}
              type='input'
              disabled={customersTableAPI?.loading}
            />
          </StyledForm>
        </StyledToolbarSearch>

        <StyledToolbarFilters>
          {/* City filter */}
          <div style={{ position: 'relative' }} ref={cityRef}>
            {selectedCity ? (
              <StyledRemovableChip
                type='button'
                aria-label={`Remove city filter: ${selectedCityName}`}
                onClick={() => { setSelectedCity(''); closeCity(); }}
              >
                {selectedCityName}
                <IconClose />
              </StyledRemovableChip>
            ) : (
              <StyledFilterChip
                type='button'
                aria-expanded={isCityOpen}
                aria-label='Filter by city'
                onClick={toggleCity}
              >
                <span className='fc-icon'><IconMapPin /></span>
                <span className='fc-label'>{intl.formatMessage({ id: 'customers.city', defaultMessage: 'City' })}</span>
                <span className='fc-chevron'><IconChevronDown /></span>
              </StyledFilterChip>
            )}
            {isCityOpen && !selectedCity && (
              <StyledFilterDropdownPanel>
                {cityOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type='button'
                    aria-selected={selectedCity === opt.value}
                    onClick={() => { setSelectedCity(opt.value); closeCity(); }}
                  >
                    {opt.label}
                  </button>
                ))}
              </StyledFilterDropdownPanel>
            )}
          </div>

          {/* Min orders filter */}
          {minOrders > 0 ? (
            <StyledRemovableChip
              type='button'
              aria-label='Remove min orders filter'
              onClick={() => setMinOrdersStr('0')}
            >
              {intl.formatMessage({ id: 'customers.minOrdersChip', defaultMessage: '{count}+ orders' }, { count: minOrders })}
              <IconClose />
            </StyledRemovableChip>
          ) : (
            <div style={{ minWidth: '8.5rem' }}>
              <SimpleSelect
                options={MIN_ORDERS_OPTIONS}
                value={minOrdersStr}
                onChange={setMinOrdersStr}
                prefixIcon={IconFilter}
                placeholder={intl.formatMessage({ id: 'customers.orderCountPlaceholder', defaultMessage: 'Order count' })}
                hideErrorMessageWrapper
              />
            </div>
          )}

          {/* Active 30d toggle */}
          <StyledToolbarGhostButton
            type='button'
            onClick={() => setActiveOnly((v) => !v)}
            style={
              activeOnly
                ? {
                    background: 'rgba(118,177,89,0.1)',
                    borderColor: 'var(--brand-green)',
                    color: 'var(--success-ink)',
                  }
                : undefined
            }
          >
            <IconCalendar />
            {intl.formatMessage({ id: 'staticRange.lastSevenDays', defaultMessage: 'Last 30 days' })}
          </StyledToolbarGhostButton>

          {hasActiveFilters && (
            <StyledToolbarGhostButton type='button' onClick={clearFilters}>
              <IconClose />
              {intl.formatMessage({
                id: 'search.clearFilters',
                defaultMessage: 'Clear',
              })}
            </StyledToolbarGhostButton>
          )}
        </StyledToolbarFilters>

        {totalCount !== undefined && (
          <StyledToolbarCount>
            {intl.formatMessage({ id: 'customers.customerCount', defaultMessage: '{count} customers' }, { count: totalCount.toLocaleString() })}
          </StyledToolbarCount>
        )}

        <div style={{ display: 'flex', gap: 4, padding: 3, borderRadius: 'var(--r-sm)', background: 'var(--paper-2)', marginInlineStart: 'auto' }}>
          <button
            type='button'
            aria-label='List view'
            aria-pressed={!isGridView}
            onClick={() => setIsGridView(false)}
            style={{
              padding: '6px 10px', borderRadius: 6, border: 0,
              background: !isGridView ? 'var(--surface)' : 'transparent',
              boxShadow: !isGridView ? 'var(--sh-1)' : 'none',
              color: !isGridView ? 'var(--ink)' : 'var(--muted)',
              fontFamily: 'inherit', fontSize: 'var(--t-sm)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <IconList />
            {intl.formatMessage({ id: 'customers.listView', defaultMessage: 'List' })}
          </button>
          <button
            type='button'
            aria-label='Grid view'
            aria-pressed={isGridView}
            onClick={() => setIsGridView(true)}
            style={{
              padding: '6px 10px', borderRadius: 6, border: 0,
              background: isGridView ? 'var(--surface)' : 'transparent',
              boxShadow: isGridView ? 'var(--sh-1)' : 'none',
              color: isGridView ? 'var(--ink)' : 'var(--muted)',
              fontFamily: 'inherit', fontSize: 'var(--t-sm)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <IconGrid />
            {intl.formatMessage({ id: 'customers.gridView', defaultMessage: 'Grid' })}
          </button>
        </div>
      </StyledToolbar>
      </ScCard>

      <ScCard pad={false} style={isGridView ? { padding: 'var(--s-4)' } : undefined}>
      {isGridView ? (
        <StyledCustomerGrid>
          {gridLoading ? (
            <div
              style={{
                gridColumn: '1 / -1',
                display: 'flex',
                justifyContent: 'center',
                padding: 'var(--s-8)',
              }}
            >
              <Spinner mode={SpinnerMode.Dark} size={SpinnerSize.Big} />
            </div>
          ) : gridRows.length === 0 ? (
            <div
              style={{
                gridColumn: '1 / -1',
                textAlign: 'center',
                padding: 'var(--s-8)',
                color: 'var(--muted)',
                fontSize: 'var(--t-sm)',
              }}
            >
              {intl.formatMessage({ id: 'customers.noData', defaultMessage: 'No customers found' })}
            </div>
          ) : (
            gridRows.map((customer) => {
              const palette = avatarPalette(customer.name || '');
              const initials = getInitials(customer.name || '');
              const city =
                customer.city || customer.addresses?.[0]?.state;
              return (
                <StyledCustomerCardItem
                  key={customer.id}
                  onClick={() => setDetailsCustomerId(customer.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <StyledCustomerAvatar
                      avatarBg={palette.bg}
                      avatarColor={palette.color}
                    >
                      {initials}
                    </StyledCustomerAvatar>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <StyledCustomerName>
                        {customer.name || (
                          <em style={{ opacity: 0.4 }}>{intl.formatMessage({ id: 'customers.unnamed', defaultMessage: 'Unnamed' })}</em>
                        )}
                      </StyledCustomerName>
                      {city && (
                        <StyledCustomerSubtitle>{city}</StyledCustomerSubtitle>
                      )}
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginTop: 4,
                    }}
                  >
                    <StyledOrderCount>
                      <strong>
                        {(customer.totalOrders ?? 0).toLocaleString()}
                      </strong>
                      <span>{intl.formatMessage({ id: 'customers.orderPlural', defaultMessage: 'orders' })}</span>
                    </StyledOrderCount>
                    <StyledValueCell>
                      <strong>
                        {(customer.totalPrice ?? 0).toLocaleString()}
                      </strong>
                      <span>IQD</span>
                    </StyledValueCell>
                  </div>
                </StyledCustomerCardItem>
              );
            })
          )}
        </StyledCustomerGrid>
      ) : (
        <StyledTableWrapper>
          <DataTable<GetCustomersResponseSingleItem>
            endpoint={apiRoutes.CUSTOMERS}
            columns={tableColumns}
            tableAPIRef={setCustomersTableAPI}
            query={{
              ...(luceneQuery ? { query: luceneQuery } : {}),
            }}
            filterItems={combinedFilterFn}
            onRowClick={({ original }) => setDetailsCustomerId(original.id)}
            queryOptions={{
              cacheTime: 0,
            }}
            placeholderProps={{
              title: intl.formatMessage({
                id: 'customers.noData',
                defaultMessage: 'No Data Found',
              }),
            }}
            pageParamKey='pageIndex'
          />
        </StyledTableWrapper>
      )}

      </ScCard>

      <AnimatePresence>
        {detailsCustomerId ? (
          <CustomerDetailsModal
            customerId={detailsCustomerId}
            onClose={() => setDetailsCustomerId(undefined)}
          />
        ) : null}
      </AnimatePresence>
    </>
  );
};

export default CustomerPage;
